import { ReactNode, forwardRef, useState } from 'react'
import { ControllerRenderProps, FieldValues } from 'react-hook-form'
import { MapPin } from 'lucide-react'
import {
  AddressData,
  Language,
  useGetApiV1AddressesSuggestQuery,
} from '@wellesley/openapi'
import { useTranslationConfigs } from '@wellesley/settings'
import { FormControl, FormItem, FormLabel, FormMessage } from '@wellesley/ui'
import Autocomplete from 'components/Autocomplete'

type AddressFieldProps = {
  value: AddressData | null | undefined
  onChange: (loc: AddressData) => void
} & Omit<
  ControllerRenderProps<FieldValues, string>,
  'value' | 'onChange' | 'ref'
>

const highlightMatch = (text: string, searchText: string): ReactNode => {
  if (!searchText || searchText.length < 3) return text

  const index = text.toLowerCase().indexOf(searchText.trim().toLowerCase())
  if (index === -1) return text

  const before = text.substring(0, index)
  const match = text.substring(index, index + searchText.length)
  const after = text.substring(index + searchText.length)

  return (
    <>
      {before}
      <span className="bg-amber-300">{match}</span>
      {after}
    </>
  )
}

export const AddressField = forwardRef<HTMLInputElement, AddressFieldProps>(
  ({ value, onChange, name, ...rest }, ref) => {
    const [searchValue, setSearchValue] = useState<string>(
      value?.formatted || value?.name || '',
    )

    const {
      isEnabled: isTranslationEnabled,
      isSettingsReady: isTranslationSettingsReady,
      preferredLanguage,
    } = useTranslationConfigs()

    const {
      data: suggestions = [],
      isLoading,
      isFetching,
    } = useGetApiV1AddressesSuggestQuery(
      {
        text: searchValue,
        ...(isTranslationEnabled && {
          lang: preferredLanguage?.toUpperCase() as Language,
        }),
      },
      {
        skip:
          !searchValue ||
          searchValue.length < 3 ||
          (isTranslationEnabled && !isTranslationSettingsReady),
      },
    )

    const handleSearch = (value: string) => {
      setSearchValue(value)
    }

    const handleSelect = (address: AddressData) => {
      onChange(address)
    }

    return (
      <FormItem className="mt-none space-y-xxs">
        <FormLabel>Location</FormLabel>
        <FormControl>
          <Autocomplete
            ref={ref}
            name={name}
            value={value || undefined}
            onChange={handleSelect}
            onInputChange={handleSearch}
            placeholder="Add event location"
            data-testid="address-field"
            icon={<MapPin size={20} />}
            options={suggestions}
            isLoading={isLoading || isFetching}
            getOptionKey={(opt) => opt.placeId + opt.formatted}
            getOptionLabel={(opt) => opt.formatted}
            renderOption={(opt) => (
              <div className="p-xxs">
                <span className="text-sm font-medium">
                  {highlightMatch(opt.formatted, searchValue)}
                </span>
              </div>
            )}
            {...rest}
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    )
  },
)

AddressField.displayName = 'AddressField'
